import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, Play, Pause, Download, Heart, Clock, Layers, Zap, Loader2, Volume2, VolumeX } from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../services/api';

const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const PreviewModal = ({ pack, isOpen, onClose, onToggleFavorite }) => {
  const videoRef = useRef(null);
  const seekRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (isOpen && videoRef.current && pack?.video_url) {
      videoRef.current.muted = false;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
      setCurrentTime(0);
    }
    return () => {
      if (videoRef.current) videoRef.current.pause();
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    };
  }, [isOpen, pack]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !duration) return;
    const rect = seekRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const pct = x / rect.width;
    const newTime = pct * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleDownloadInternal = async () => {
    if (pack?.storage_account === 'C') return;
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      await api.downloadPack(pack.id);
    } catch (err) {
      alert("Erro ao processar download. Verifique sua conexão.");
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen || !pack) return null;

  const isFavorite = pack.isFavorite || pack.is_favorite;
  const is4K = pack.is4K || pack.is_4k;
  const videoUrl = pack.video_url;
  const isReferenceOnly = pack.storage_account === 'C';
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed inset-0 z-[200] flex flex-col md:items-center md:justify-center md:p-4 font-['Helvetica',_sans-serif]">
      <div
        className="absolute inset-0 bg-[#05050A]/98 backdrop-blur-xl"
        onClick={onClose}
      />

      <div className="relative flex flex-col w-full h-full md:max-w-5xl md:h-auto md:max-h-[90vh] md:flex-row bg-[#0A0A12] md:rounded-3xl overflow-hidden shadow-2xl border border-white/5">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[210] p-3 bg-black/60 hover:bg-white/10 backdrop-blur-md rounded-full text-white transition-all active:scale-90"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Vídeo */}
        <div
          className="relative w-full md:w-3/5 bg-black flex flex-col items-center justify-center overflow-hidden"
          style={{ minHeight: '45vw', maxHeight: '55vh' }}
        >
          {videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-contain"
              loop
              playsInline
              autoPlay
              preload="metadata"
              onClick={togglePlay}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              style={{ WebkitPlaysinline: true }}
            />
          ) : (
            <div className="text-slate-500 p-8">Sem mídia disponível</div>
          )}

          {/* Overlay play/pause */}
          {!isPlaying && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={togglePlay}
            >
              <div className="w-16 h-16 bg-[#0056D2]/80 rounded-full flex items-center justify-center backdrop-blur-sm shadow-xl">
                <Play className="w-8 h-8 text-white ml-1" fill="white" />
              </div>
            </div>
          )}

          {/* Barra de controles */}
          {videoUrl && (
            <div className="absolute bottom-0 left-0 right-0 px-3 pb-2 pt-6 bg-gradient-to-t from-black/80 to-transparent">
              {/* Seekbar */}
              <div
                ref={seekRef}
                className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-2 group"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-[#0056D2] rounded-full relative transition-all"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              {/* Controles inferiores */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={togglePlay}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-white"
                  >
                    {isPlaying
                      ? <Pause className="w-4 h-4" fill="white" />
                      : <Play className="w-4 h-4 ml-0.5" fill="white" />
                    }
                  </button>

                  <button
                    onClick={toggleMute}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-white"
                  >
                    {isMuted
                      ? <VolumeX className="w-4 h-4" />
                      : <Volume2 className="w-4 h-4" />
                    }
                  </button>
                </div>

                <span className="text-white/60 text-[11px] font-mono tabular-nums">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Informações */}
        <div className="flex-1 overflow-y-auto md:w-2/5 p-5 md:p-8 flex flex-col bg-[#0A0A0C] border-t md:border-t-0 md:border-l border-white/10 rounded-t-[28px] md:rounded-none -mt-6 md:mt-0 relative z-10">
          <div className="flex items-start justify-between mb-5">
            <div className="flex-1 pr-2">
              <h2 className="text-white text-lg md:text-2xl font-bold tracking-tight uppercase italic leading-tight">
                {pack.title}
              </h2>
              <p className="text-[#0056D2] text-[10px] uppercase tracking-widest font-bold mt-1">
                Corte Cinematográfico Premium
              </p>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                if (typeof onToggleFavorite === 'function') onToggleFavorite(pack.id);
              }}
              className={cn(
                "p-3 rounded-full transition-all flex-shrink-0",
                isFavorite
                  ? "bg-[#0056D2] text-white shadow-lg"
                  : "bg-white/5 text-slate-400"
              )}
            >
              <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5">
              <Layers className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-[10px] text-slate-500 uppercase font-bold">Coleção</span>
                <span className="text-white text-xs font-bold truncate">{pack.collection}</span>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5">
              <Zap className="w-4 h-4 text-[#0056D2] flex-shrink-0" />
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500 uppercase font-bold">Qualidade</span>
                <span className="text-white text-xs font-bold">{is4K ? '4K Ultra HD' : 'HD'}</span>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5 col-span-2">
              <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500 uppercase font-bold">Duração</span>
                <span className="text-white text-xs font-bold">{pack.duration?.toFixed(1)} segundos</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-auto space-y-3 pb-4 md:pb-0">
            {isReferenceOnly ? (
              <div className="w-full py-4 rounded-2xl border border-dashed border-white/10 text-center">
                <span className="text-[11px] uppercase tracking-widest font-bold text-[#0056D2] opacity-70">
                  Conteúdo apenas para referência
                </span>
              </div>
            ) : (
              <>
                <button
                  onClick={handleDownloadInternal}
                  disabled={isDownloading}
                  className={cn(
                    "w-full bg-[#0056D2] hover:bg-[#0066FF] text-white font-bold py-4 rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-[#0056D2]/20 active:scale-[0.98]",
                    isDownloading && "opacity-70 cursor-not-allowed"
                  )}
                >
                  {isDownloading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  <span className="uppercase tracking-wider">
                    {isDownloading ? 'Processando...' : 'Baixar Cena Agora'}
                  </span>
                </button>

                <p className="text-center text-[10px] text-slate-600 font-bold uppercase tracking-tighter">
                  {isDownloading ? 'Aguarde o início do download...' : 'Download em alta resolução'}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
